import LabeledSwitch from "@/components/LabeledSwitch";
import { Button } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ElevatedPaper from "@/components/ElevatedPaper";
import Flex from "@/components/Flex";
import PageLayout from "@/components/PageLayout";
import Text from "@/components/Text";
import CustomTabs from "@/components/app/CustomTabs";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { useRoleController } from "@/components/useRoleController";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { useGetUnreadChatCount } from "@/queries/communication/chat/convo/use-get-unread-chat-count";
import { useSearchPatientList } from "@/queries/list/useSearchPatientList";
import useFcmChatEventListener from "@/services/fcm/listeners/FcmChatEventListener";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  roleFilter,
} from "@/utils/utils";
import {
  ContactDictionaryFilters,
  SmsTextFilterState,
} from "./components/FilterComponents";
import {
  CONTACT_DICTIONARY_FILTER_INITIAL_VALUES,
  ContactDictionaryFilter,
} from "./contact-dictionary/contact-dictionary-constant";
import SendBulkEmailModal from "./email/SendBulkEmailModal";
import UploadFaxDocumentDrawer from "./fax/UploadFaxDocumentDrawer";
import SendBulkSMSModal from "./messages/SendBulkSMSModal";

const CommunicationLayout = () => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    const currentTab = location.pathname.split("/").pop();
    return currentTab === "communications" ? "" : (currentTab as string);
  });
  const navigate = useNavigate();
  const { hasView: hasViewChat } = useRoleController("Communication > Chat");
  const { hasView: hasViewEmail } = useRoleController("Communication > eMail");
  const { hasView: hasViewSms } = useRoleController("Communication > SMS");

  // Filter states for different tabs
  const [smsFilters, setSmsFilters] = useState<SmsTextFilterState>(
    CONTACT_DICTIONARY_FILTER_INITIAL_VALUES.sms
  );
  const [contactDictionaryFilters, setContactDictionaryFilters] =
    useState<ContactDictionaryFilter>(
      CONTACT_DICTIONARY_FILTER_INITIAL_VALUES.contact
    );

  // Generic filter change handlers
  const handleFilterChange = useCallback(
    (filterType: string, key: string, value: any) => {
      const setterMap = {
        sms: setSmsFilters,
        dictionary: setContactDictionaryFilters,
      };

      const setter = setterMap[filterType as keyof typeof setterMap];
      if (setter) {
        setter((prev: any) => ({ ...prev, [key]: value }));
      }
    },
    []
  );

  // Generic search handlers
  const handleSearch = useCallback((filterType: string, query: string) => {
    const setterMap = {
      sms: setSmsFilters,
      dictionary: setContactDictionaryFilters,
    };

    const setter = setterMap[filterType as keyof typeof setterMap];
    if (setter) {
      setter((prev: any) => ({
        ...prev,
        searchQuery: query,
        archive: filterType === "dictionary" ? false : prev.archive,
      }));
    }
  }, []);

  // Specific handlers for backward compatibility and type safety
  const handleSmsFilterChange = useCallback(
    (key: string, value: string) => {
      handleFilterChange("sms", key, value);
    },
    [handleFilterChange]
  );

  const handleContactDictionaryFilterChange = useCallback(
    (key: string, value: { value: string; label: string }) => {
      handleFilterChange("dictionary", key, value);
    },
    [handleFilterChange]
  );

  const handleSmsSearch = useCallback(
    (query: string) => {
      handleSearch("sms", query);
    },
    [handleSearch]
  );

  const handleContactDictionarySearch = useCallback(
    (query: string) => {
      handleSearch("dictionary", query);
    },
    [handleSearch]
  );

  const [
    contactDictionaryDisclosure,
    sendMailDisclosure,
    uploadFaxDocumentDisclosure,
    sendSMSDisclosure,
  ] = useReduceDisclosure(4);

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [isArcheivedEmail, setIsArcheivedEmail] = useState<boolean>(false);

  // note : dont optimise this API as this patient list is using at may locations.
  const {
    data: patientList,
    isLoading: patientListLoading,
    isRefetching: patientListRefetching,
  } = useSearchPatientList({
    pagination,
    xTENANTID: getXtenantId(),
  });

  const handleSendMail = () => {
    const filteredPatients = patientList?.data?.content
      ?.filter((it) => rowSelectionModel?.includes(it?.uuid))
      .map((it) => ({
        uuid: it?.uuid,
        name: `${it?.firstName || ""}  ${it?.lastName || ""}`,
      }));

    sendMailDisclosure.open(filteredPatients);
  };

  const handleSendSMS = () => {
    sendSMSDisclosure.open();
  };

  // Function to render the appropriate filter component based on active tab
  const renderFilters = () => {
    switch (activeTab) {
      case "":
        return (
          <>
            <StyledButtonV2
              variant="contained"
              onClick={handleSendSMS}
              shouldAddIcon={true}
              label="New SMS"
            />
            {sendSMSDisclosure.opened && (
              <SendBulkSMSModal disClosureHost={sendSMSDisclosure} />
            )}
          </>
        );
      case "email":
        return (
          <Flex width={"100%"} justifyContent={"flex-end"}>
            <Flex gap={2}>
              {rowSelectionModel?.length ? (
                <>
                  <Flex gap={2} alignItems={"center"}>
                    <Text>{`${rowSelectionModel.length} Selected`}</Text>
                  </Flex>
                  <Button
                    variant="outlined"
                    sx={{
                      width: "80px",
                    }}
                    onClick={() => setRowSelectionModel([])}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: "148px" }}
                    onClick={handleSendMail}
                  >
                    Send Emails
                  </Button>
                </>
              ) : (
                <>
                  <LabeledSwitch
                    label="Show Archived Emails"
                    checked={isArcheivedEmail}
                    onChange={(e) => setIsArcheivedEmail(e.target.checked)}
                  />
                  <StyledButtonV2
                    variant="contained"
                    onClick={() => sendMailDisclosure.open()}
                    shouldAddIcon
                    label="Compose Email"
                  />
                </>
              )}
            </Flex>
            {sendMailDisclosure.opened && (
              <SendBulkEmailModal
                disClosureHost={sendMailDisclosure}
                onSave={() => setRowSelectionModel([])}
              />
            )}
          </Flex>
        );
      case "dictionary":
        return (
          <ContactDictionaryFilters
            filters={contactDictionaryFilters}
            onFilterChange={handleContactDictionaryFilterChange as any}
            onSearch={handleContactDictionarySearch}
            contactDictionaryDisclosure={contactDictionaryDisclosure}
          />
        );
      case "fax":
        return (
          <>
            {uploadFaxDocumentDisclosure.opened && (
              <UploadFaxDocumentDrawer
                disclosureHost={uploadFaxDocumentDisclosure}
              />
            )}
            <StyledButtonV2
              variant="contained"
              onClick={() => uploadFaxDocumentDisclosure.open()}
              shouldAddIcon
              label="Send Fax"
            />
          </>
        );
      default:
        return null;
    }
  };

  const [me] = useCurrentUser();
  const { refetch: refetchUnreadChatCount } = useGetUnreadChatCount({
    providerId: me?.uuid,
  });

  // The push carries one conversation's count, not the provider's total, so re-read the total.
  const [chatCount, setChatFcmCount] = useFcmChatEventListener({
    onChatNotification: () => refetchUnreadChatCount(),
  });

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <Flex
            alignItems={"center"}
            width={"100%"}
            borderBottom={"1px solid rgb(229, 224, 224)"}
          >
            <Flex flexShrink={0}>
              <CustomTabs
                tabsData={roleFilter({
                  list: [
                    {
                      value: "",
                      label: "SMS/Text",
                    },
                    {
                      value: "chat",
                      label: "Chat",
                      count: chatCount,
                    },
                    {
                      value: "email",
                      label: "Email",
                    },
                    {
                      value: "dictionary",
                      label: "Contact Dictionary",
                    },
                    {
                      value: "fax",
                      label: "Fax",
                    },
                  ],
                  compWith: {
                    chat: hasViewChat,
                    email: hasViewEmail,
                    sms: hasViewSms,
                  },
                })}
                value={activeTab}
                onChange={(v) => {
                  setActiveTab(v);
                  navigate(v);
                }}
              />
            </Flex>
            <Flex width={"100%"} justifyContent={"flex-end"}>
              {renderFilters()}
            </Flex>
          </Flex>
        }
      >
        <Outlet
          context={{
            setChatFcmCount,
            // sms releated data are passed to SmsText
            smsFilters,
            handleSmsFilterChange,
            handleSmsSearch,
            // contact dictionary releated data are passed to ContactDictionary
            contactDictionaryFilters,
            handleContactDictionaryFilterChange,
            handleContactDictionarySearch,
            contactDictionaryDisclosure,
            // email releated data are passed to EmailTable
            sendMailDisclosure,
            rowSelectionModel,
            setRowSelectionModel,
            isArcheivedEmail,
            pagination,
            setPagination,
            patientList,
            patientListLoading,
            patientListRefetching,
          }}
        />
      </PageLayout>
    </ElevatedPaper>
  );
};

export default CommunicationLayout;

// export const TABS_DATA = ;
